import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  X, Plus, Trash2, Save, Loader2, Shield, Lock, 
  ChevronDown, FileText, Edit3, Clock
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CounselingNotes({ onClose }) {
  const [templates, setTemplates] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [noteTitle, setNoteTitle] = useState('');
  const [noteFields, setNoteFields] = useState({});
  const [saving, setSaving] = useState(false);
  const [editingNote, setEditingNote] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [tRes, nRes] = await Promise.all([
        axios.get(`${API}/counseling/templates`),
        axios.get(`${API}/counseling/notes`),
      ]);
      setTemplates(tRes.data.templates || []);
      setNotes(nRes.data.notes || []);
    } catch (err) {
      console.error('Load counseling data error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const startNewNote = (template) => {
    setSelectedTemplate(template);
    setNoteTitle('');
    setNoteFields({});
    setEditingNote(null);
    setView('edit');
  };

  const openExistingNote = (note) => {
    const tmpl = templates.find(t => t.id === note.template_id);
    setSelectedTemplate(tmpl || templates[0]);
    setNoteTitle(note.title);
    setNoteFields(note.fields || {});
    setEditingNote(note);
    setView('edit');
  };

  const saveNote = async () => {
    if (!noteTitle.trim()) return;
    setSaving(true);
    try {
      if (editingNote) {
        await axios.put(`${API}/counseling/notes/${editingNote.id}`, {
          title: noteTitle.trim(),
          fields: noteFields,
          template_id: selectedTemplate?.id,
        });
      } else {
        await axios.post(`${API}/counseling/notes`, {
          title: noteTitle.trim(),
          fields: noteFields,
          template_id: selectedTemplate?.id,
        });
      }
      await loadData();
      setView('list');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const deleteNote = async (id) => {
    if (!window.confirm('Delete this note? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/counseling/notes/${id}`);
      setNotes(notes.filter(n => n.id !== id));
    } catch (err) {
      alert('Failed to delete');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="counseling-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="p-5 border-b border-[#E2DFD8]">
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-xl font-bold text-[#1B3A6B] flex items-center gap-2">
              <Shield className="w-5 h-5" /> Counseling Notes
            </h2>
            <button onClick={onClose} className="p-2 hover:bg-[#F4F2EC] rounded-lg" data-testid="counseling-close-btn">
              <X className="w-5 h-5 text-[#475569]" />
            </button>
          </div>
          <p className="text-xs text-[#94A3B8] flex items-center gap-1"><Lock className="w-3 h-3" /> Private & confidential — only you can see your notes</p>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#1B3A6B] mx-auto" /></div>
          ) : view === 'list' ? (
            <div>
              {/* Template picker */}
              <h3 className="text-xs font-bold uppercase tracking-widest text-[#94A3B8] mb-3">New Note from Template</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-6">
                {templates.map(t => (
                  <button
                    key={t.id}
                    onClick={() => startNewNote(t)}
                    className="p-3 text-left border border-[#E2DFD8] rounded-xl hover:border-[#1B3A6B]/30 hover:shadow-sm transition-all"
                    data-testid={`template-${t.id}`}
                  >
                    <p className="text-sm font-medium text-[#0F1A2A]">{t.name}</p>
                    <p className="text-[10px] text-[#94A3B8] mt-0.5">{t.fields.length} fields</p>
                  </button>
                ))}
              </div>

              {/* Existing notes */}
              <h3 className="text-xs font-bold uppercase tracking-widest text-[#94A3B8] mb-3">Your Notes ({notes.length})</h3>
              {notes.length === 0 ? (
                <p className="text-sm text-[#94A3B8] text-center py-6">No counseling notes yet. Select a template above to start.</p>
              ) : (
                <div className="space-y-2">
                  {notes.map(note => {
                    const tmpl = templates.find(t => t.id === note.template_id);
                    return (
                      <div key={note.id} className="flex items-center gap-3 p-3 bg-[#F9F8F6] rounded-xl group" data-testid={`note-${note.id}`}>
                        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => openExistingNote(note)}>
                          <p className="text-sm font-medium text-[#0F1A2A] truncate">{note.title}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-[10px] text-[#B8860B] font-medium">{tmpl?.name || 'General'}</span>
                            <span className="text-[10px] text-[#94A3B8] flex items-center gap-1">
                              <Clock className="w-3 h-3" /> {new Date(note.updated_at || note.created_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={() => openExistingNote(note)} className="p-1.5 hover:bg-white rounded"><Edit3 className="w-3.5 h-3.5 text-[#475569]" /></button>
                          <button onClick={() => deleteNote(note.id)} className="p-1.5 hover:bg-red-50 rounded"><Trash2 className="w-3.5 h-3.5 text-[#475569] hover:text-red-500" /></button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            /* Edit View */
            <div data-testid="counseling-edit-view">
              <button onClick={() => setView('list')} className="text-xs text-[#475569] hover:text-[#1B3A6B] mb-4 flex items-center gap-1">
                <ChevronDown className="w-3 h-3 rotate-90" /> Back to notes
              </button>
              
              <div className="mb-3 flex items-center gap-2">
                <span className="px-2.5 py-0.5 bg-[#1B3A6B]/10 text-[#1B3A6B] rounded-full text-xs font-medium">{selectedTemplate?.name}</span>
              </div>

              <input
                value={noteTitle}
                onChange={e => setNoteTitle(e.target.value)}
                placeholder="Session title (e.g., Smith Family - Session 3)"
                className="w-full px-4 py-3 text-sm border border-[#E2DFD8] rounded-lg focus:ring-2 focus:ring-[#1B3A6B] mb-4 font-medium"
                data-testid="note-title-input"
              />

              <div className="space-y-3">
                {selectedTemplate?.fields.map((field, idx) => (
                  <div key={idx}>
                    <label className="block text-xs font-medium text-[#475569] mb-1">{field}</label>
                    <textarea
                      value={noteFields[field] || ''}
                      onChange={e => setNoteFields({ ...noteFields, [field]: e.target.value })}
                      rows={field.includes('Notes') || field.includes('Concerns') || field.includes('Progress') ? 3 : 1}
                      className="w-full px-3 py-2 text-sm border border-[#E2DFD8] rounded-lg focus:ring-2 focus:ring-[#1B3A6B] resize-none"
                      data-testid={`note-field-${idx}`}
                    />
                  </div>
                ))}
              </div>

              <div className="flex gap-2 justify-end mt-5">
                <button onClick={() => setView('list')} className="px-4 py-2 text-sm text-[#475569] border border-[#E2DFD8] rounded-lg">Cancel</button>
                <button
                  onClick={saveNote}
                  disabled={saving || !noteTitle.trim()}
                  className="flex items-center gap-2 px-5 py-2 bg-[#1B3A6B] text-white rounded-lg text-sm font-medium disabled:opacity-50 hover:bg-[#254B8A]"
                  data-testid="save-note-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {editingNote ? 'Update' : 'Save'} Note
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
